import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Globe, Link2, Loader2, Sparkles, 
  ArrowRight, AlertCircle, History, Trash2, Eye,
  Copy, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import AppLayout from '../components/AppLayout';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PressReleaseWriter() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [companyUrl, setCompanyUrl] = useState('');
  const [pressReleaseUrl1, setPressReleaseUrl1] = useState('');
  const [pressReleaseUrl2, setPressReleaseUrl2] = useState('');
  const [pressReleaseUrl3, setPressReleaseUrl3] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/press-releases`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGenerate = async () => {
    if (!companyUrl.trim()) {
      setError('Please enter the company URL');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/press-releases/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          company_url: companyUrl,
          press_release_url_1: pressReleaseUrl1 || null,
          press_release_url_2: pressReleaseUrl2 || null,
          press_release_url_3: pressReleaseUrl3 || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate press release');
      }

      const result = await response.json();
      // Navigate to the result page
      navigate(`/press-releases/${result.press_release_id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this press release?')) return;

    try {
      await fetch(`${BACKEND_URL}/api/press-releases/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setHistory(history.filter(pr => pr.press_release_id !== id));
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  return (
    <AppLayout title="Press Release Writer">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-[#A2FF00]/10 rounded-xl">
            <FileText className="h-8 w-8 text-[#A2FF00]" />
          </div>
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Press Release Writer</h1>
            <p className={isDark ? 'text-zinc-400' : 'text-gray-600'}>AI-powered press release generation for new members</p>
          </div>
        </div>

        {/* Main Form Card */}
        <Card className={`${isDark ? 'bg-zinc-900/50 border-zinc-800' : 'bg-white border-gray-200 shadow-sm'} mb-8`}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Sparkles className="h-5 w-5 text-[#A2FF00]" />
              Generate New Press Release
            </CardTitle>
            <CardDescription className={isDark ? 'text-zinc-400' : 'text-gray-600'}>
              Enter the company URL and optional sample press releases for style reference
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company URL - Required */}
            <div className="space-y-2">
              <label className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
                <Globe className="h-4 w-4 text-[#A2FF00]" />
                Company Website URL <span className="text-red-500">*</span>
              </label>
              <Input
                value={companyUrl}
                onChange={(e) => setCompanyUrl(e.target.value)}
                placeholder="https://company-website.com"
                className={isDark 
                  ? 'bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500' 
                  : 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400'}
                disabled={isGenerating}
              />
              <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
                The website of the company joining QSECDEF
              </p>
            </div>

            {/* Press Release URLs - Optional */}
            <div className="space-y-4">
              <label className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
                <Link2 className={`h-4 w-4 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`} />
                Sample Press Releases (Optional - for style reference)
              </label>
              
              <div className="grid gap-3">
                <Input
                  value={pressReleaseUrl1}
                  onChange={(e) => setPressReleaseUrl1(e.target.value)}
                  placeholder="Press Release URL 1"
                  className={isDark 
                    ? 'bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500' 
                    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400'}
                  disabled={isGenerating}
                />
                <Input
                  value={pressReleaseUrl2}
                  onChange={(e) => setPressReleaseUrl2(e.target.value)}
                  placeholder="Press Release URL 2"
                  className={isDark 
                    ? 'bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500' 
                    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400'}
                  disabled={isGenerating}
                />
                <Input
                  value={pressReleaseUrl3}
                  onChange={(e) => setPressReleaseUrl3(e.target.value)}
                  placeholder="Press Release URL 3"
                  className={isDark 
                    ? 'bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500' 
                    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400'}
                  disabled={isGenerating}
                />
              </div>
              <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
                Links to the company&apos;s existing press releases to mimic their style
              </p>
            </div>

            {/* Error Display */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={isGenerating || !companyUrl.trim()}
              className="w-full bg-[#A2FF00] text-black hover:bg-[#8FE600] font-semibold h-12"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Generating Press Release...
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 mr-2" />
                  Generate Press Release
                  <ArrowRight className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>

            {isGenerating && (
              <div className={`text-center text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                <p>This may take up to 60 seconds...</p>
                <p className="text-xs mt-1">AI is fetching content, writing, and refining your press release</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* History Section */}
        <Card className={isDark ? 'bg-zinc-900/50 border-zinc-800' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <History className={`h-5 w-5 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`} />
              Recent Press Releases
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className={`h-6 w-6 animate-spin ${isDark ? 'text-zinc-400' : 'text-gray-500'}`} />
              </div>
            ) : history.length === 0 ? (
              <div className={`text-center py-8 ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No press releases generated yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((pr) => (
                  <div
                    key={pr.press_release_id}
                    onClick={() => navigate(`/press-releases/${pr.press_release_id}`)}
                    className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                      isDark 
                        ? 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600' 
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <FileText className="h-5 w-5 text-[#A2FF00] flex-shrink-0" />
                      <div className="overflow-hidden">
                        <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{pr.company_url}</p>
                        <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
                          {new Date(pr.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/press-releases/${pr.press_release_id}`);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={isDark ? 'text-zinc-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'}
                        onClick={(e) => handleDelete(pr.press_release_id, e)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
